import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql/error';
import { get } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { closeModals } from '../components-v2/Modal';
import { clearFlash } from '../variables';

const getErrorCode = (graphQLError: GraphQLError) => get(graphQLError, 'extensions.code') as string | null;

const getMessageForGraphQLError = (graphQLError: GraphQLError) => {
  const code = getErrorCode(graphQLError);
  const message = graphQLError.message;

  // For errors that bubble up from the Prisma middleware, we can just check the message
  if (message.includes('Please delete this grouped transaction from the movement management page instead.')) {
    return (
      <>
        Please delete this grouped transaction from the{' '}
        <Link
          className="underline"
          to="/recon/cost-basis/movements"
          onClick={() => {
            closeModals();
            clearFlash();
          }}
        >
          movement management page
        </Link>{' '}
        instead.
      </>
    );
  }

  switch (code) {
    case 'YEAR_LOCKED': {
      return (
        <>
          Tax years are locked through the year {get(graphQLError, 'extensions.lockedThroughYear')}. You can
          edit your tax details in the{' '}
          <Link
            className="underline"
            to="/settings/tax-details"
            onClick={() => {
              closeModals();
              clearFlash();
            }}
          >
            settings
          </Link>
          .
        </>
      );
    }
    case 'BATCH_UPDATE_IN_PROGRESS': {
      return <>Batch transaction update is in progress. Please try again in a few minutes.</>;
    }
    case 'BATCH_DELETE_IN_PROGRESS': {
      return <>Batch transaction delete is in progress. Please try again in a few minutes.</>;
    }
    case 'RECONCILIATION_IN_PROGRESS': {
      return <>Reconciliation is in progress. Please try again in a few minutes.</>;
    }
    case 'DATA_MODEL_V2_UPGRADE_IN_PROGRESS': {
      return <>Your account is being upgraded. Please try again once the upgrade has finished.</>;
    }
    case 'DATA_MODEL_V2_UPGRADE_BLOCKED': {
      const blockingJobs = get(graphQLError, 'extensions.blockingJobs') as string[] | undefined;
      return blockingJobs?.length ? (
        <>
          Your account can’t be upgraded while these are in progress: {blockingJobs.join(', ')}. Please try
          again once they have finished.
        </>
      ) : (
        <>
          Your account can’t be upgraded while other work on it is in progress. Please try again in a few
          minutes.
        </>
      );
    }
    case 'ACCOUNT_DELETION_FAILED': {
      return <>{graphQLError.message}</>;
    }
  }
  return [];
};

export const getApolloErrorMessageCodes = (error: unknown) => {
  if (!(error instanceof ApolloError)) return null;
  const { graphQLErrors } = error;

  return graphQLErrors.flatMap(getErrorCode);
};

export const getApolloErrorMessages = (error: unknown) => {
  if (!(error instanceof ApolloError)) return null;
  const { graphQLErrors } = error;

  const messages = graphQLErrors.flatMap(getMessageForGraphQLError);
  return messages.length > 0 ? messages : null;
};
